import {
  ControlsContainer,
  MapCardArrow,
  MapCardBackdrop,
  MapCardContainer,
  MapContainer,
  MapLegendContainer,
  MapLoading,
  StyledIconButton,
  StyledTableMaxContent,
} from './styles';
import {
  FilterList,
  MapOutlined,
  MyLocation as MyLocationIcon,
  Search,
  StorageOutlined,
} from '@mui/icons-material';
import { ScoreTable } from '@/components/organisms/score/table';
import { useCallback, useEffect, useRef, useState, lazy, Suspense } from 'react';

const Map = lazy(() =>
  import('@/components/organisms/map').then((module) => ({
    default: module.Map,
  }))
);
import { useGeometry } from '@/hooks/useGeometry';
import { Tabs } from '@/components/molecules/tabs';
import { FloatingButtons } from '@/components/molecules/floatingButtons';
import { PageLayout } from '@/components/molecules/pageLayout';
import { CustomHeader } from '@/components/molecules/customHeader';
import { CircularProgress, Tooltip, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from '@/hooks/useTranslation';
import { Text } from '@/components/atoms/text';
import { SearchMunicipalityBottomSheet } from '@/components/organisms/searchMunicipality/bottomsheet';
import { SafeMarkdown } from '@/components/atoms/markdown/safe';
import { useAdvancedFiltersUrl } from '@/hooks/useAdvancedFiltersUrl';
import { AppRouters } from '@/utils/constants/routers';
import { MapCard } from '@/components/molecules/cards/map';
import type { GeometryData } from '@/context/geometry';
import { KEYS } from '@/utils/constants/keys';
import { guideDriver } from '../../guide';
import { steps } from '../guide/steps';
import { Layers } from '../layers';
import { useLanguage } from '@/hooks/useLanguage';
import { useLocation } from 'react-router-dom';
import { LegendButton } from '@/components/molecules/legendButton';
import { shouldRefetchGeometryOnReturnToMap } from '@/utils/map/shouldRefetchGeometryOnReturnToMap';
import { useTerritorialDashboardMapFilters } from '../useTerritorialDashboardMapFilters';
import type { MunicipalityWithSearch } from '@/graphql/types/municipality';

interface TerritorialDashboardMobileProps {
  variant: 'risk' | 'opportunity';
  onChangeVariant: (variant: 'risk' | 'opportunity') => void;
}

export const TerritorialDashboardMobile = ({
  variant,
  onChangeVariant,
}: TerritorialDashboardMobileProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const {
    geometry,
    loading,
    getGeometry,
    getGeometryForMap,
    setIsMunicipality,
    isMunicipality,
    clearSavedMapData,
    resetFitBounds,
    setStartSearch,
  } = useGeometry();

  const { t } = useTranslation();
  const { getFiltersFromUrl, setFiltersToUrl } = useAdvancedFiltersUrl();
  const { currentLanguage } = useLanguage();
  const location = useLocation();

  const [tabValue, setTabValue] = useState<string | number>('map');
  const [loadingData, setLoadingData] = useState(false);
  const [selectedLayer, setSelectedLayer] = useState<number>(() => {
    const initialFilters = getFiltersFromUrl();
    if (initialFilters.dynamicFilters?.find((f) => f.field === 'dimension_id')) {
      return initialFilters.dynamicFilters?.find((f) => f.field === 'dimension_id')
        ?.value as number;
    }
    return -10;
  });
  const [selectedBigqueryId, setSelectedBigqueryId] = useState<string>('');
  const [selectedDimensionName, setSelectedDimensionName] = useState<string>('');
  const [openSearch, setOpenSearch] = useState(false);
  const [searchModalVariant, setSearchModalVariant] = useState<'advanced' | 'municipality'>();
  const [showMapCard, setShowMapCard] = useState(false);
  const [selectedMunicipalityData, setSelectedMunicipalityData] = useState<GeometryData | null>(
    null
  );
  const [zoomToMunicipalityTrigger, setZoomToMunicipalityTrigger] = useState(0);

  const hasInitialGeometryLoaded = useRef(false);
  const isInitialMount = useRef(true);
  const prevTabValueRef = useRef<string | number>('map');

  const isLoading = loading;

  const {
    handleLayerChange,
    refetchGeometryForMapState,
    floatingButtonsVariant,
  } = useTerritorialDashboardMapFilters({
    variant,
    selectedBigqueryId,
    selectedLayer,
    setSelectedLayerState: setSelectedLayer,
    isMunicipality,
    currentLanguage,
    setLoadingData,
    isInitialMountRef: isInitialMount,
  });

  const handleVariant = (variant: 'risk' | 'opportunity') => {
    onChangeVariant(variant);
  };

  const handleOpenMunicipalitySearch = () => {
    setSearchModalVariant('municipality');
    setOpenSearch(true);
  };

  const handleOpenAdvancedSearch = () => {
    setSearchModalVariant('advanced');
    setOpenSearch(true);
  };

  const handleMunicipalitySearch = useCallback(
    async (municipality: MunicipalityWithSearch | null) => {
      if (!municipality) {
        return;
      }

      const currentFilters = getFiltersFromUrl();
      const dynamicFilters = (currentFilters.dynamicFilters || []).filter(
        (filter) => filter.field !== 'municipality_code'
      );
      const additionalMunicipalityCodes = Array.from(
        new Set([
          ...(currentFilters.additionalMunicipalityCodes || []),
          municipality.municipality_code,
        ])
      );
      const nextFilters = {
        ...currentFilters,
        dynamicFilters,
        additionalMunicipalityCodes,
      };

      setIsMunicipality(false);
      setStartSearch(true);
      clearSavedMapData();
      resetFitBounds();
      setFiltersToUrl(AppRouters.home, nextFilters);

      try {
        setLoadingData(true);
        await getGeometry({
          filters: nextFilters,
          pagination: { limit: 5600 },
          language: currentLanguage,
        });
      } finally {
        setLoadingData(false);
      }
    },
    [
      clearSavedMapData,
      currentLanguage,
      getFiltersFromUrl,
      getGeometry,
      resetFitBounds,
      setFiltersToUrl,
      setIsMunicipality,
      setStartSearch,
    ]
  );

  const driverObj = useRef<ReturnType<typeof guideDriver> | null>(null);

  useEffect(() => {
    if (isInitialMount.current) {
      return;
    }
    void refetchGeometryForMapState();
  }, [
    currentLanguage,
    floatingButtonsVariant,
    isMunicipality,
    refetchGeometryForMapState,
    selectedBigqueryId,
    selectedLayer,
  ]);

  useEffect(() => {
    const prev = prevTabValueRef.current;
    if (
      shouldRefetchGeometryOnReturnToMap(isInitialMount.current, prev, tabValue, 'map', 'table')
    ) {
      void refetchGeometryForMapState();
    }
    prevTabValueRef.current = tabValue;
  }, [tabValue, refetchGeometryForMapState]);

  useEffect(() => {
    if (!hasInitialGeometryLoaded.current) {
      hasInitialGeometryLoaded.current = true;
      isInitialMount.current = false;

      const searchParams = new URLSearchParams(location.search);
      const hasFilters = searchParams.toString().length > 0;

      const loadInitialData = async () => {
        try {
          setLoadingData(true);

          if (hasFilters) {
            const urlFilters = getFiltersFromUrl();
            const municipalityCode = searchParams.get('municipality_code');
            if ((urlFilters.additionalMunicipalityCodes || []).length > 0) {
              setIsMunicipality(false);
              const mapDynamicFilters = (urlFilters.dynamicFilters || []).filter(
                (f) => f.field !== 'municipality_code'
              );

              await getGeometry({
                filters: {
                  dynamicFilters: mapDynamicFilters,
                  dimensionLevelFilters: urlFilters.dimensionLevelFilters,
                  additionalMunicipalityCodes: urlFilters.additionalMunicipalityCodes,
                },
                pagination: { limit: 5600 },
                language: currentLanguage,
              });
            } else if (municipalityCode) {
              setIsMunicipality(true);
              await getGeometry({
                filters: {
                  dynamicFilters: [
                    ...(urlFilters.dynamicFilters || []),
                    {
                      field: 'municipality_code',
                      operator: 'eq',
                      value: municipalityCode,
                    },
                  ],
                  dimensionLevelFilters: urlFilters.dimensionLevelFilters,
                },
                pagination: { limit: 1 },
                language: currentLanguage,
              });
            } else {
              const channel =
                selectedBigqueryId === 'overall' || !selectedBigqueryId
                  ? 'general'
                  : selectedBigqueryId;
              const mapDynamicFilters = (urlFilters.dynamicFilters || []).filter(
                (f) => f.field !== 'dimension_id' && f.field !== 'municipality_code'
              );

              await getGeometryForMap({
                filters: {
                  type: floatingButtonsVariant,
                  channel,
                  dynamicFilters: mapDynamicFilters,
                  dimensionLevelFilters: urlFilters.dimensionLevelFilters,
                },
                language: currentLanguage,
              });
            }
          } else {
            await getGeometryForMap({
              filters: {
                type: floatingButtonsVariant,
                channel: 'general',
              },
              language: currentLanguage,
            });
          }
        } finally {
          setLoadingData(false);
        }
      };

      void loadInitialData();
    }
  }, [
    currentLanguage,
    floatingButtonsVariant,
    getFiltersFromUrl,
    getGeometry,
    getGeometryForMap,
    location.search,
    selectedBigqueryId,
    setIsMunicipality,
    tabValue,
  ]);

  // Monitora quando os dados de geometria são carregados para exibir o MapCard
  useEffect(() => {
    if (geometry.length > 0 && geometry.length === 1) {
      // Se há apenas um município (resultado de busca específica)
      const municipalityData = geometry[0];
      setSelectedMunicipalityData(municipalityData);
      setShowMapCard(true);
    }
  }, [geometry]);

  useEffect(() => {
    document.title = 'KYT-e - Interactive Map';
  }, []);

  useEffect(() => {
    if (localStorage.getItem(KEYS.TERRITORIAL_DASHBOARD_TUTORIAL)) {
      return;
    }

    if (t('label.label.guide.step_1.text') === 'label.label.guide.step_1.text') {
      return;
    }

    if (!driverObj.current) {
      driverObj.current = guideDriver({ t, steps, isMobile });
      driverObj.current.drive();
    }
  }, [t]);

  const tabs = [
    {
      label: t('tabs.tabs.table.interactive_map'),
      value: 'map',
      tab: (
        <>
          <MapContainer>
            <Suspense
              fallback={
                <MapLoading>
                  <CircularProgress size={20} color="inherit" />
                  <Text variant="body2" color="text.primary">
                    <SafeMarkdown text={t('messages.loading.message.loading')} />
                  </Text>
                </MapLoading>
              }
            >
              <Map
                loadingData={loadingData}
                geometry={geometry}
                variant={floatingButtonsVariant}
                hasDimensionsScores={selectedLayer !== -10}
                selectedBigqueryId={selectedBigqueryId}
                selectedDimensionId={selectedLayer !== -10 ? selectedLayer : undefined}
                selectedDimensionName={selectedDimensionName}
                zoomToMunicipalityTrigger={zoomToMunicipalityTrigger}
              />
            </Suspense>

            {showMapCard && selectedMunicipalityData && (
              <>
                {/* Backdrop para fechar ao clicar fora */}
                <MapCardBackdrop
                  onClick={() => {
                    setShowMapCard(false);
                    setSelectedMunicipalityData(null);
                  }}
                />

                {/* MapCard centralizado */}
                <MapCardContainer onClick={(e) => e.stopPropagation()}>
                  <MapCard
                    loadingData={loadingData}
                    variant={floatingButtonsVariant}
                    geometryData={selectedMunicipalityData}
                    hasDimensionsScores={selectedLayer !== -10}
                    href={`${AppRouters.municipality}/${selectedMunicipalityData.municipality_code}`}
                    onClose={() => {
                      setShowMapCard(false);
                      setSelectedMunicipalityData(null);
                    }}
                  />
                  {/* Seta indicativa apontando para o município */}
                  <MapCardArrow />
                </MapCardContainer>
              </>
            )}

            <MapLegendContainer>
              <ControlsContainer>
                <LegendButton variant={floatingButtonsVariant} />

                <Layers
                  selectedLayer={selectedLayer}
                  setSelectedLayer={handleLayerChange}
                  setSelectedBigqueryId={setSelectedBigqueryId}
                  setSelectedDimensionName={setSelectedDimensionName}
                />

                {isMunicipality && geometry.length === 1 && (
                  <Tooltip title={t('button.button.show_on_map')}>
                    <StyledIconButton
                      onClick={() => setZoomToMunicipalityTrigger(Date.now())}
                      aria-label={t('button.button.show_on_map')}
                    >
                      <MyLocationIcon color="action" />
                    </StyledIconButton>
                  </Tooltip>
                )}

                <StyledIconButton onClick={handleOpenAdvancedSearch} id="advanced_search">
                  <FilterList color="action" />
                </StyledIconButton>

                <StyledIconButton onClick={handleOpenMunicipalitySearch} id="simple_search">
                  <Search color="action" />
                </StyledIconButton>
              </ControlsContainer>
            </MapLegendContainer>
          </MapContainer>
        </>
      ),
      icon: MapOutlined,
    },
    {
      label: t('tabs.tabs.table.table'),
      value: 'table',
      tab: (
        <StyledTableMaxContent>
          <ScoreTable variant={floatingButtonsVariant} />
        </StyledTableMaxContent>
      ),
      icon: StorageOutlined,
    },
  ];

  return (
    <>
      <PageLayout.Root
        header={
          <CustomHeader
            headerTitle={() => ({
              title: t('page.dashboard.dashboard.title'),
            })}
            showSearchButtons
            onMunicipalitySearch={handleOpenMunicipalitySearch}
            onAdvancedSearch={handleOpenAdvancedSearch}
          />
        }
      >
        <Tabs
          tabs={tabs}
          initialValue={tabValue}
          isMobile={isMobile}
          showBackground
          id="menu_tabs"
          onGetTabValue={setTabValue}
        />

        <FloatingButtons
          variant={floatingButtonsVariant}
          onClick={handleVariant}
          id="floating_buttons"
          loading={isLoading}
          disabled={{
            risk: selectedBigqueryId === 'economica',
            opportunity: selectedBigqueryId === 'seguranca',
          }}
        />
      </PageLayout.Root>

      {openSearch && (
        <SearchMunicipalityBottomSheet
          open={openSearch}
          variant={searchModalVariant}
          onClose={() => setOpenSearch(false)}
          onMunicipalityChange={(municipality) => void handleMunicipalitySearch(municipality)}
        />
      )}
    </>
  );
};
